import { useState, useEffect, useMemo } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import MasterComponent from "../../../components/Forms"
import FormSetup from "../../../components/Forms/FormSetup"
import { MasterConfigs, FormColumnConfig } from "../../../interfaces/entity"
import { Button, Upload, Modal, message, notification, Card, Row, Col, Typography, Tag, Empty, Spin, Form, Alert, Dropdown, Menu, Switch, DatePicker, Space, Select, Input, Table } from "antd"
import { FileExcelOutlined, UploadOutlined, CalendarOutlined, ProjectOutlined, ClockCircleOutlined, DownloadOutlined, PaperClipOutlined, FileOutlined, MoreOutlined, EyeOutlined, PlusOutlined } from "@ant-design/icons"
import axios from "axios"
import moment from "moment"
import dayjs from "dayjs"
import { postData, putData } from "../../../actions/developer"
import { getData, uploadFile } from "../../../actions/apiCall"
import { useDispatch } from "react-redux"
import Papa from "papaparse"
import * as XLSX from "xlsx"

const { Text, Title } = Typography

// Wrapper component to handle mobile form - uses FormSetup directly
const MobileFormWrapper = ({ config, onClose }: { config: MasterConfigs, onClose: () => void }) => {
    return (
        <FormSetup
            id={null}
            isView={false}
            config={config}
            open={true}
            onClose={onClose}
            isModal={true}
        />
    );
};

const TimesheetList = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const accessToken = searchParams.get('token') || localStorage.getItem('timesheet_access_token');
    const [uploadModalVisible, setUploadModalVisible] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [mappingStep, setMappingStep] = useState<'upload' | 'mapping' | 'processing'>('upload');
    const [excelFile, setExcelFile] = useState<File | null>(null);
    const [excelColumns, setExcelColumns] = useState<string[]>([]);
    const [excelData, setExcelData] = useState<any[]>([]);
    const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
    const [dateFormat, setDateFormat] = useState<string>('auto'); // auto-detect or user-selected format
    const [fixedValues, setFixedValues] = useState<Record<string, string>>({}); // Fixed values for fields (e.g., projectName, module)
    const [fieldMappingMode, setFieldMappingMode] = useState<Record<string, 'map' | 'fixed'>>({}); // 'map' = from Excel, 'fixed' = fixed value
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [timesheets, setTimesheets] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [formVisible, setFormVisible] = useState(false);
    const [uploadingAttachment, setUploadingAttachment] = useState<string | null>(null);
    const [isWeekendSelected, setIsWeekendSelected] = useState(false);
    const [existingTimesheetDate, setExistingTimesheetDate] = useState<string | null>(null);
    const [entryType, setEntryType] = useState<'workday' | 'leave' | 'holiday'>('workday');
    const [selectedMonth, setSelectedMonth] = useState(dayjs());

    // Derived states for backward compatibility
    const isLeave = entryType === 'leave';
    const isHoliday = entryType === 'holiday';

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Check if initial date is a weekend and if timesheet exists
    useEffect(() => {
        const today = dayjs();
        const dayOfWeek = today.day();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        setIsWeekendSelected(isWeekend);
        // Check if timesheet exists for today
        if (accessToken) {
            checkExistingTimesheetForDate(today);
        }
    }, [accessToken]);

    // Get selected month dates for filter
    const getMonthStart = (month: any) => month.startOf('month').format('YYYY-MM-DD');
    const getMonthEnd = (month: any) => month.endOf('month').format('YYYY-MM-DD');
    const [currentMonthStart, setCurrentMonthStart] = useState(getMonthStart(selectedMonth));
    const [currentMonthEnd, setCurrentMonthEnd] = useState(getMonthEnd(selectedMonth));

    // Update dates when selected month changes
    useEffect(() => {
        setCurrentMonthStart(getMonthStart(selectedMonth));
        setCurrentMonthEnd(getMonthEnd(selectedMonth));
    }, [selectedMonth]);

    const loadTimesheets = async () => {
        if (!accessToken) return;
        setLoading(true);
        try {
            // Calculate dates directly from selectedMonth to avoid timing issues
            const start = getMonthStart(selectedMonth);
            const end = getMonthEnd(selectedMonth);

            const params = new URLSearchParams({
                accessToken: accessToken || '',
                startDate: start,
                endDate: end
            });
            const response = await dispatch(getData(`/timesheet/public/my-timesheets?${params.toString()}`));
            if (response.code === 200) {
                // Handle different response structures
                let timesheetData = [];
                if (response.data?.timesheets) {
                    timesheetData = response.data.timesheets;
                } else if (Array.isArray(response.data)) {
                    timesheetData = response.data;
                } else if (response.data?.data) {
                    timesheetData = Array.isArray(response.data.data) ? response.data.data : [];
                }

                // Deduplicate by _id to prevent duplicate records
                const uniqueTimesheets = timesheetData.filter((item: any, index: number, self: any[]) => {
                    const id = item._id || item.id;
                    if (!id) return true; // Keep items without ID
                    return index === self.findIndex((t: any) => (t._id || t.id) === id);
                });

                setTimesheets(uniqueTimesheets);
            }
        } catch (error) {
            console.error('Error loading timesheets:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isMobile && accessToken) {
            loadTimesheets();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile, accessToken, selectedMonth]);

    useEffect(() => {
        // Listen for table refresh events
        const handleRefresh = () => {
            if (isMobile) {
                loadTimesheets();
            }
        };
        window.addEventListener('REFRESH_TABLE', handleRefresh);
        return () => {
            window.removeEventListener('REFRESH_TABLE', handleRefresh);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile, accessToken]);

    // Refresh table when month changes (for desktop view)
    useEffect(() => {
        if (!isMobile && accessToken) {
            // Trigger table refresh when month changes
            dispatch({
                type: 'REFRESH_TABLE',
                refreshTable: true
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMonth]);

    const handleExport = async () => {
        if (!accessToken) return;

        try {
            // Calculate dates directly from selectedMonth
            const start = getMonthStart(selectedMonth);
            const end = getMonthEnd(selectedMonth);

            const params = new URLSearchParams({
                accessToken: accessToken
            });
            params.append('startDate', start);
            params.append('endDate', end);

            const response = await axios.get(`${process.env.API_URL}/timesheet/public/export?${params.toString()}`, {
                responseType: 'blob'
            });

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const filename = `timesheet_${start}_to_${end}_${moment().format('YYYY-MM-DD')}.xlsx`;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            notification.success({
                message: 'Timesheet exported successfully',
                type: 'success'
            });
        } catch (error) {
            console.error('Error exporting timesheet:', error);
            notification.error({
                message: 'Failed to export timesheet',
                type: 'error'
            });
        }
    };

    // Required columns for timesheet
    const requiredColumns = [
        { key: 'projectName', label: 'Project Name', required: true },
        { key: 'date', label: 'Date', required: true },
        { key: 'module', label: 'Module', required: true },
        { key: 'taskDetails', label: 'Task Details', required: true },
        { key: 'workedHours', label: 'Worked Hours', required: true },
        { key: 'entryType', label: 'Entry Type (Workday/Leave/Holiday)', required: false }
    ];

    const parseExcelFile = async (file: File) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target?.result as ArrayBuffer);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                    if (jsonData.length === 0) {
                        reject(new Error('Excel file is empty'));
                        return;
                    }

                    // First row contains headers
                    const headers = (jsonData[0] as any[]).map((h: any) => String(h || '').trim()).filter(Boolean);
                    const rows = jsonData.slice(1).map((row: any[]) => {
                        const obj: any = {};
                        headers.forEach((header, index) => {
                            obj[header] = row[index] || '';
                        });
                        return obj;
                    });

                    resolve({ headers, rows });
                } catch (error) {
                    reject(error);
                }
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsArrayBuffer(file);
        });
    };

    const handleFileUpload = async (file: File) => {
        if (!accessToken) {
            message.error('Access token required');
            return;
        }

        setExcelFile(file);
        setUploadLoading(true);

        try {
            const fileExtension = file.name.split('.').pop()?.toLowerCase();

            if (fileExtension === 'xlsx' || fileExtension === 'xls') {
                // Parse Excel file
                const result: any = await parseExcelFile(file);
                setExcelColumns(result.headers);
                setExcelData(result.rows);
                setMappingStep('mapping');

                // Initialize mapping with exact matches
                const initialMapping: Record<string, string> = {};
                requiredColumns.forEach(reqCol => {
                    const match = result.headers.find((h: string) =>
                        h.toLowerCase() === reqCol.key.toLowerCase() ||
                        h.toLowerCase() === reqCol.label.toLowerCase()
                    );
                    if (match) {
                        initialMapping[reqCol.key] = match;
                    }
                });
                setColumnMapping(initialMapping);
            } else if (fileExtension === 'csv') {
                // Parse CSV file
                const text = await file.text();
                const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });

                if (parsed.errors.length > 0) {
                    message.error('Error parsing CSV file');
                    setUploadLoading(false);
                    return;
                }

                const headers = Object.keys(parsed.data[0] || {});
                const rows = parsed.data as any[];

                setExcelColumns(headers);
                setExcelData(rows);
                setMappingStep('mapping');

                // Initialize mapping with exact matches
                const initialMapping: Record<string, string> = {};
                requiredColumns.forEach(reqCol => {
                    const match = headers.find((h: string) =>
                        h.toLowerCase() === reqCol.key.toLowerCase() ||
                        h.toLowerCase() === reqCol.label.toLowerCase()
                    );
                    if (match) {
                        initialMapping[reqCol.key] = match;
                    }
                });
                setColumnMapping(initialMapping);
            } else {
                message.error('Please upload a CSV or Excel file (.csv, .xlsx, .xls)');
                setUploadLoading(false);
                return;
            }
        } catch (error) {
            console.error('Error parsing file:', error);
            message.error('Failed to parse file. Please check the file format.');
            setUploadLoading(false);
        } finally {
            setUploadLoading(false);
        }
    };

    // Generate preview data
    const generatePreview = () => {
        const previewRows: any[] = [];
        const maxPreviewRows = Math.min(10, excelData.length); // Show max 10 rows for preview

        for (let i = 0; i < maxPreviewRows; i++) {
            const row = excelData[i];
            const previewRow: any = {
                key: i,
                rowNumber: i + 1,
                errors: []
            };

            // Map Excel columns to required columns or use fixed values
            requiredColumns.forEach(reqCol => {
                const mode = fieldMappingMode[reqCol.key] || 'map';
                let value = '';

                if (mode === 'fixed') {
                    value = fixedValues[reqCol.key] || '';
                } else {
                    const excelCol = columnMapping[reqCol.key];
                    if (excelCol && row[excelCol] !== undefined && row[excelCol] !== null && row[excelCol] !== '') {
                        value = row[excelCol];
                    }
                }

                // Special handling for date
                if (reqCol.key === 'date') {
                    const dateMode = fieldMappingMode.date || 'map';
                    let parsedDate = '';

                    if (dateMode === 'fixed' && fixedValues.date) {
                        parsedDate = parseDate(fixedValues.date, 'auto');
                        if (!parsedDate) {
                            const momentDate = moment(fixedValues.date);
                            if (momentDate.isValid()) {
                                parsedDate = momentDate.format('YYYY-MM-DD');
                            }
                        }
                    } else {
                        parsedDate = parseDate(value, dateFormat);
                    }

                    if (!parsedDate || parsedDate.startsWith('1970-01-01')) {
                        previewRow.errors.push(`Invalid date: ${value}`);
                        previewRow.date = value || fixedValues.date || '';
                        previewRow.dateParsed = '❌ Invalid';
                    } else {
                        previewRow.date = value || fixedValues.date || '';
                        previewRow.dateParsed = parsedDate;
                    }
                } else {
                    previewRow[reqCol.key] = value;
                }

                // Validate required fields
                if (reqCol.required && !value) {
                    previewRow.errors.push(`${reqCol.label} is required`);
                }
            });

            // Validate worked hours
            const workedHoursValue = previewRow.workedHours;
            if (workedHoursValue === undefined || workedHoursValue === null || workedHoursValue === '') {
                previewRow.errors.push('Worked Hours is required');
            } else if (isNaN(parseFloat(String(workedHoursValue)))) {
                previewRow.errors.push('Worked Hours must be a number');
            }

            previewRow.hasErrors = previewRow.errors.length > 0;
            previewRows.push(previewRow);
        }

        return previewRows;
    };

    // Get existing timesheets for date range to check for duplicates
    const getExistingTimesheets = async (startDate: string, endDate: string) => {
        if (!accessToken) return {};

        try {
            const params = new URLSearchParams({
                accessToken: accessToken,
                startDate: startDate,
                endDate: endDate
            });
            const response = await dispatch(getData(`/timesheet/public/my-timesheets?${params.toString()}`));
            if (response.code === 200) {
                const data = response.data?.timesheets || response.data?.data || response.data || [];
                const timesheetData = Array.isArray(data) ? data : (data.timesheets || []);
                // Create a map of date -> timesheet for quick lookup
                const dateMap: Record<string, any> = {};
                timesheetData.forEach((ts: any) => {
                    const dateStr = moment(ts.date).format('YYYY-MM-DD');
                    dateMap[dateStr] = ts;
                });
                return dateMap;
            }
        } catch (error) {
            console.error('Error fetching existing timesheets:', error);
        }
        return {};
    };

    // Generate all dates in a date range
    const getAllDatesInRange = (startDate: string, endDate: string): string[] => {
        const dates: string[] = [];
        const start = moment(startDate);
        const end = moment(endDate);
        const current = start.clone();

        while (current.isSameOrBefore(end)) {
            dates.push(current.format('YYYY-MM-DD'));
            current.add(1, 'day');
        }
        return dates;
    };

    // Check if a date is a weekend
    const isWeekendDate = (dateStr: string): boolean => {
        const date = moment(dateStr);
        const dayOfWeek = date.day();
        return dayOfWeek === 0 || dayOfWeek === 6; // Sunday or Saturday
    };

    const handleBulkUpload = async () => {
        if (!accessToken || !excelFile || !excelData.length) {
            message.error('Missing required data');
            return;
        }

        setMappingStep('processing');
        setUploadLoading(true);

        try {
            let successCount = 0;
            let errorCount = 0;
            let updateCount = 0;
            let missingDatesCreatedCount = 0;
            let weekendAutoCreatedCount = 0;

            // Collect all dates from Excel to determine date range
            const excelDates: string[] = [];
            const processedDates = new Set<string>();
            const dateToRowMap: Record<string, any> = {}; // Map date to Excel row data

            // First pass: process Excel data and collect dates
            for (const row of excelData) {
                try {
                    // Map Excel columns to required columns or use fixed values
                    const mappedRow: any = {};
                    requiredColumns.forEach(reqCol => {
                        const mode = fieldMappingMode[reqCol.key] || 'map';

                        if (mode === 'fixed') {
                            // Use fixed value if provided
                            if (fixedValues[reqCol.key]) {
                                mappedRow[reqCol.key] = fixedValues[reqCol.key];
                            }
                        } else {
                            // Map from Excel column
                            const excelCol = columnMapping[reqCol.key];
                            if (excelCol && row[excelCol] !== undefined && row[excelCol] !== null && row[excelCol] !== '') {
                                mappedRow[reqCol.key] = row[excelCol];
                            }
                        }
                    });

                    // Validate required fields (check both mapped values and fixed values)
                    const hasProjectName = mappedRow.projectName || fixedValues.projectName;
                    const hasDate = mappedRow.date || fixedValues.date;

                    if (!hasProjectName || !hasDate) {
                        errorCount++;
                        continue;
                    }

                    const entryTypeValue = mappedRow.entryType?.toLowerCase() || 'workday';
                    const isLeaveValue = entryTypeValue === 'leave';
                    const isHolidayValue = entryTypeValue === 'holiday';

                    // Parse date using detected/selected format
                    // If date is from fixed value, try to parse it directly
                    const dateMode = fieldMappingMode.date || 'map';
                    let parsedDate = '';

                    if (dateMode === 'fixed' && fixedValues.date) {
                        // For fixed date, try to parse it
                        parsedDate = parseDate(fixedValues.date, 'auto');
                        if (!parsedDate) {
                            // If auto fails, try moment directly
                            const momentDate = moment(fixedValues.date);
                            if (momentDate.isValid()) {
                                parsedDate = momentDate.format('YYYY-MM-DD');
                            }
                        }
                    } else {
                        // Date from Excel column
                        parsedDate = parseDate(mappedRow.date, dateFormat);
                    }

                    if (!parsedDate) {
                        console.error('Failed to parse date:', mappedRow.date || fixedValues.date, 'with format:', dateFormat);
                        errorCount++;
                        continue;
                    }

                    // Check if this date is a weekend - for weekends, module, taskDetails, and workedHours are optional
                    const isWeekend = isWeekendDate(parsedDate);
                    const hasModule = mappedRow.module || fixedValues.module;
                    const hasTaskDetails = mappedRow.taskDetails || fixedValues.taskDetails;
                    const hasWorkedHours = mappedRow.workedHours !== undefined || fixedValues.workedHours !== undefined;

                    // For non-weekend, non-leave, non-holiday days, all fields are required
                    if (!isWeekend && !isLeaveValue && !isHolidayValue) {
                        if (!hasModule || !hasTaskDetails || !hasWorkedHours) {
                            errorCount++;
                            continue;
                        }
                    }

                    excelDates.push(parsedDate);
                    processedDates.add(parsedDate);

                    // Store row data for this date
                    dateToRowMap[parsedDate] = {
                        mappedRow,
                        entryTypeValue,
                        isLeaveValue,
                        isHolidayValue
                    };
                } catch (error) {
                    console.error('Error processing row:', error);
                    errorCount++;
                }
            }

            // Get date range for fetching existing timesheets
            if (excelDates.length > 0) {
                const minDate = moment.min(excelDates.map(d => moment(d))).format('YYYY-MM-DD');
                const maxDate = moment.max(excelDates.map(d => moment(d))).format('YYYY-MM-DD');

                // Get existing timesheets for the date range
                const existingTimesheets = await getExistingTimesheets(minDate, maxDate);

                // Process each date from Excel
                for (const parsedDate of excelDates) {
                    try {
                        const rowData = dateToRowMap[parsedDate];
                        if (!rowData) continue;

                        const { mappedRow, entryTypeValue, isLeaveValue, isHolidayValue } = rowData;

                        // Use fixed values if available, otherwise use mapped values
                        const projectName = fixedValues.projectName || mappedRow.projectName;

                        // Check if this date is a weekend
                        const isWeekend = isWeekendDate(parsedDate);

                        // For weekends, use defaults if not provided
                        let module = isLeaveValue ? 'Leave' : (isHolidayValue ? 'Holiday' : (fixedValues.module || mappedRow.module || (isWeekend ? 'Weekend' : '')));
                        let taskDetails = isLeaveValue ? 'Leave for the day' : (isHolidayValue ? 'Holiday for the day' : (fixedValues.taskDetails || mappedRow.taskDetails || (isWeekend ? 'Weekend work' : '')));
                        let workedHours = isLeaveValue || isHolidayValue ? 0 : (isWeekend && !fixedValues.workedHours && mappedRow.workedHours === undefined ? 0 : parseFloat(fixedValues.workedHours || mappedRow.workedHours || '0'));

                        const payload = {
                            accessToken: accessToken,
                            projectName: projectName,
                            date: parsedDate,
                            module: module,
                            taskDetails: taskDetails,
                            workedHours: workedHours,
                            isLeave: isLeaveValue,
                            isHoliday: isHolidayValue
                        };

                        // Check if timesheet already exists for this date
                        const existingTimesheet = existingTimesheets[parsedDate];

                        if (existingTimesheet) {
                            // Update existing timesheet - use submit endpoint which should handle updates
                            // The backend should check for duplicates and update if exists
                            const resData = await dispatch(postData('/timesheet/public/submit', payload));
                            if (resData.code == 200) {
                                updateCount++;
                            } else {
                                errorCount++;
                            }
                        } else {
                            // Create new timesheet
                            const resData = await dispatch(postData('/timesheet/public/submit', payload));
                            if (resData.code == 200) {
                                successCount++;
                            } else {
                                errorCount++;
                            }
                        }
                    } catch (error) {
                        console.error('Error processing date:', parsedDate, error);
                        errorCount++;
                    }
                }

                // Auto-create entries for ALL missing dates (not just weekends)
                // Goal: ensure every day between first and last date has an entry
                const allDatesInRange = getAllDatesInRange(minDate, maxDate);
                let missingDatesCreatedCount = 0;

                for (const dateStr of allDatesInRange) {
                    // Skip if already processed from Excel or already exists in database
                    if (!processedDates.has(dateStr) && !existingTimesheets[dateStr]) {
                        try {
                            const isWeekend = isWeekendDate(dateStr);

                            // Goal: Create entries for ALL missing dates to keep records for all days
                            // For weekends: use defaults if fixed values not provided
                            // For non-weekends: use fixed values if provided, otherwise create blank entry
                            const projectName = fixedValues.projectName || (isWeekend ? 'Weekend' : 'Blank Entry');
                            const module = fixedValues.module || (isWeekend ? 'Weekend' : '');
                            const taskDetails = fixedValues.taskDetails || (isWeekend ? 'Weekend work' : '');
                            const workedHours = parseFloat(fixedValues.workedHours || '0');

                            // Always create entry for missing dates (blank entries for non-weekends if no fixed values)
                            const missingDatePayload = {
                                accessToken: accessToken,
                                projectName: projectName,
                                date: dateStr,
                                module: module,
                                taskDetails: taskDetails,
                                workedHours: workedHours,
                                isLeave: false,
                                isHoliday: false
                            };

                            const resData = await dispatch(postData('/timesheet/public/submit', missingDatePayload));
                            if (resData.code == 200) {
                                missingDatesCreatedCount++;
                                if (isWeekend) {
                                    weekendAutoCreatedCount++;
                                }
                            }
                        } catch (error) {
                            console.error('Error creating missing date entry:', dateStr, error);
                        }
                    }
                }
            }

            const messageParts = [];
            if (successCount > 0) messageParts.push(`${successCount} created`);
            if (updateCount > 0) messageParts.push(`${updateCount} updated`);
            if (missingDatesCreatedCount > 0) messageParts.push(`${missingDatesCreatedCount} missing dates auto-created`);
            if (errorCount > 0) messageParts.push(`${errorCount} failed`);

            const messageText = `Upload completed: ${messageParts.join(', ')}`;
            message.success(messageText);

            setUploadModalVisible(false);
            setMappingStep('upload');
            setExcelFile(null);
            setExcelColumns([]);
            setExcelData([]);
            setColumnMapping({});
            setDateFormat('auto');
            setFixedValues({});
            setFieldMappingMode({});

            // Refresh table
            dispatch({
                type: 'REFRESH_TABLE',
                refreshTable: true
            });
            if (isMobile) {
                loadTimesheets();
            }
        } catch (error) {
            console.error('Error uploading bulk timesheet:', error);
            message.error('Failed to upload timesheet');
        } finally {
            setUploadLoading(false);
            setMappingStep('upload');
        }
    };

    // Common date formats to try
    const commonDateFormats = [
        'YYYY-MM-DD',
        'DD/MM/YYYY',
        'MM/DD/YYYY',
        'DD-MM-YYYY',
        'MM-DD-YYYY',
        'YYYY/MM/DD',
        'DD.MM.YYYY',
        'MM.DD.YYYY',
        'DD MMM YYYY',
        'MMM DD, YYYY',
        'DD-MMM-YYYY',
        'YYYY-MM-DD HH:mm:ss',
        'DD/MM/YYYY HH:mm:ss',
        'MM/DD/YYYY HH:mm:ss'
    ];

    // Detect date format from sample data
    const detectDateFormat = (dateValue: any): string => {
        if (!dateValue) return 'YYYY-MM-DD';

        // Check if it's a number (Excel serial date)
        const numValue = Number(dateValue);
        if (!isNaN(numValue) && numValue > 0) {
            // Excel serial dates are typically between 1 (Jan 1, 1900) and ~50000+ (modern dates)
            // Numbers in this range are likely Excel serial dates
            if (numValue >= 1 && numValue < 1000000) {
                return 'excel';
            }
        }

        const dateStr = String(dateValue).trim();

        // If it's already a valid date object or ISO string, return auto
        if (moment(dateValue, moment.ISO_8601, true).isValid()) {
            return 'auto';
        }

        // Try common formats
        for (const format of commonDateFormats) {
            if (moment(dateStr, format, true).isValid()) {
                return format;
            }
        }

        // Default to auto-detect
        return 'auto';
    };

    // Parse date with format
    const parseDate = (dateValue: any, format: string): string => {
        if (dateValue === null || dateValue === undefined || dateValue === '') return '';

        try {
            // Check if it's a number (Excel serial date) - handle this first
            const numValue = Number(dateValue);
            if (!isNaN(numValue) && numValue > 0 && (format === 'excel' || format === 'auto')) {
                // Excel serial date conversion:
                // Excel day 1 = Jan 1, 1900
                // Excel incorrectly treats 1900 as a leap year (includes Feb 29, 1900 which didn't exist)
                // So for dates after Feb 28, 1900, we need to subtract 1 day

                // Excel epoch: Dec 30, 1899 (day 0), but day 1 = Jan 1, 1900
                // For dates >= 60 (which is Feb 29, 1900 in Excel), subtract 1 to account for the non-existent leap day
                let adjustedDays = numValue - 1; // Convert to days since Dec 30, 1899
                if (numValue >= 60) {
                    adjustedDays = numValue - 2; // Subtract extra day for the non-existent Feb 29, 1900
                }

                const excelEpoch = new Date(Date.UTC(1899, 11, 30)); // Dec 30, 1899 UTC
                const jsDate = new Date(excelEpoch.getTime() + adjustedDays * 24 * 60 * 60 * 1000);

                // Check if result is valid and reasonable
                const formatted = moment(jsDate).format('YYYY-MM-DD');
                if (formatted && !formatted.startsWith('1970-01-01') && !formatted.startsWith('1899-')) {
                    return formatted;
                }

                // Fallback: Try simpler conversion without leap year adjustment
                const epoch1900 = new Date(Date.UTC(1900, 0, 1));
                const altJsDate = new Date(epoch1900.getTime() + (numValue - 2) * 24 * 60 * 60 * 1000);
                const altFormatted = moment(altJsDate).format('YYYY-MM-DD');
                if (altFormatted && !altFormatted.startsWith('1970-01-01')) {
                    return altFormatted;
                }
            }

            // Handle Excel serial date format explicitly
            if (format === 'excel') {
                const serialDate = Number(dateValue);
                if (isNaN(serialDate)) return '';

                // Excel serial date conversion with leap year adjustment
                let adjustedDays = serialDate - 1; // Convert to days since Dec 30, 1899
                if (serialDate >= 60) {
                    adjustedDays = serialDate - 2; // Subtract extra day for the non-existent Feb 29, 1900
                }

                const excelEpoch = new Date(Date.UTC(1899, 11, 30));
                const jsDate = new Date(excelEpoch.getTime() + adjustedDays * 24 * 60 * 60 * 1000);
                const formatted = moment(jsDate).format('YYYY-MM-DD');

                if (formatted && !formatted.startsWith('1970-01-01') && !formatted.startsWith('1899-')) {
                    return formatted;
                }

                // Fallback
                const epoch1900 = new Date(Date.UTC(1900, 0, 1));
                const altJsDate = new Date(epoch1900.getTime() + (serialDate - 2) * 24 * 60 * 60 * 1000);
                return moment(altJsDate).format('YYYY-MM-DD');
            }

            // Auto-detect format
            if (format === 'auto') {
                // Try ISO format first
                if (moment(dateValue, moment.ISO_8601, true).isValid()) {
                    return moment(dateValue).format('YYYY-MM-DD');
                }

                // Try common formats
                for (const fmt of commonDateFormats) {
                    const parsed = moment(dateValue, fmt, true);
                    if (parsed.isValid()) {
                        return parsed.format('YYYY-MM-DD');
                    }
                }

                // Fallback to moment's default parsing
                const parsed = moment(dateValue);
                if (parsed.isValid()) {
                    return parsed.format('YYYY-MM-DD');
                }
            } else {
                // Use specified format
                const parsed = moment(dateValue, format, true);
                if (parsed.isValid()) {
                    return parsed.format('YYYY-MM-DD');
                }
            }
        } catch (error) {
            console.error('Error parsing date:', dateValue, format, error);
        }

        return '';
    };

    const downloadTemplate = () => {
        const template = [
            ['projectName', 'date', 'module', 'taskDetails', 'workedHours'],
            ['Project A', '2024-01-15', 'Module 1', 'Task description', '8'],
            ['Project B', '2024-01-16', 'Module 2', 'Task description', '6']
        ];

        const csvContent = template.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'timesheet_template.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    const checkExistingTimesheetForDate = async (selectedDate: dayjs.Dayjs) => {
        if (!accessToken || !selectedDate) {
            setExistingTimesheetDate(null);
            return;
        }

        try {
            const dateStr = selectedDate.format('YYYY-MM-DD');
            const params = new URLSearchParams({
                accessToken: accessToken,
                startDate: dateStr,
                endDate: dateStr
            });
            const response = await dispatch(getData(`/timesheet/public/my-timesheets?${params.toString()}`));
            if (response.code === 200) {
                const data = response.data?.data || response.data || [];
                const timesheetData = Array.isArray(data) ? data : (data.timesheets || []);
                if (timesheetData.length > 0) {
                    setExistingTimesheetDate(dateStr);
                } else {
                    setExistingTimesheetDate(null);
                }
            } else {
                setExistingTimesheetDate(null);
            }
        } catch (error) {
            console.error('Error checking existing timesheet:', error);
            setExistingTimesheetDate(null);
        }
    };

    const handleApprovalUpload = async (file: File, timesheetId: string) => {
        if (!accessToken) {
            message.error('Access token is required');
            return;
        }

        // Validate file size (max 10MB)
        const isLt10M = file.size / 1024 / 1024 < 10;
        if (!isLt10M) {
            message.error('File must be smaller than 10MB!');
            return;
        }

        setUploadingAttachment(timesheetId);
        try {
            // Upload file
            const formData = new FormData();
            formData.append('file', file);
            const uploadResult = await uploadFile('/upload/add', formData);

            if (uploadResult.status === 'SUCCESS' && uploadResult.files && uploadResult.files.length > 0) {
                const fileUrl = uploadResult.files[0];

                // Update timesheet with approval attachment
                const updateResult = await dispatch(putData('/timesheet/public/update-approval', {
                    accessToken: accessToken,
                    timesheetId: timesheetId,
                    approvalAttachment: fileUrl
                }));

                // putData returns response.data.data when code is 200, which should be { message, timesheet }
                // or response.data when code is not 200 (which has code property)
                console.log('Update approval attachment result:', updateResult);

                // Check if updateResult has timesheet property (success case)
                // API returns: { message: '...', timesheet: {...} } on success
                if (updateResult && updateResult.timesheet) {
                    // Success - updateResult has timesheet property
                    message.success('Approval attachment uploaded successfully');

                    // Force table refresh by toggling refreshTable state
                    dispatch({
                        type: 'REFRESH_TABLE',
                        refreshTable: false
                    });
                    // Small delay to ensure state update, then trigger refresh
                    setTimeout(() => {
                        dispatch({
                            type: 'REFRESH_TABLE',
                            refreshTable: true
                        });
                        // Also trigger window event for mobile view
                        window.dispatchEvent(new Event('REFRESH_TABLE'));
                    }, 100);

                    // Refresh mobile cards if on mobile
                    if (isMobile) {
                        setTimeout(() => {
                            loadTimesheets();
                        }, 200);
                    }
                } else if (updateResult && updateResult.code) {
                    // Error - updateResult has code property
                    const errorMessage = updateResult?.message || updateResult?.error || 'Failed to update approval attachment';
                    message.error(errorMessage);
                    console.error('Update approval attachment error:', updateResult);
                } else {
                    // Unexpected response format
                    console.warn('Unexpected update result format:', updateResult);
                    // Still try to refresh the table
                    message.success('Approval attachment uploaded successfully');
                    dispatch({
                        type: 'REFRESH_TABLE',
                        refreshTable: false
                    });
                    setTimeout(() => {
                        dispatch({
                            type: 'REFRESH_TABLE',
                            refreshTable: true
                        });
                        window.dispatchEvent(new Event('REFRESH_TABLE'));
                    }, 100);
                    if (isMobile) {
                        setTimeout(() => {
                            loadTimesheets();
                        }, 200);
                    }
                }
            } else {
                message.error('Failed to upload file');
            }
        } catch (error: any) {
            console.error('Error uploading approval attachment:', error);
            message.error(error.message || 'Failed to upload approval attachment');
        } finally {
            setUploadingAttachment(null);
        }
    };

    const config: MasterConfigs = useMemo(() => {
        // Calculate dates directly from selectedMonth to ensure they're always in sync
        const start = getMonthStart(selectedMonth);
        const end = getMonthEnd(selectedMonth);

        // Compute derived values inside useMemo
        const isLeaveValue = entryType === 'leave';
        const isHolidayValue = entryType === 'holiday';

        return {
            api: () => {
                // Return base URL without query params - they'll be added via queryParams
                return `/timesheet/public/my-timesheets`;
            },
            queryParams: {
                accessToken: accessToken || '',
                startDate: start,
                endDate: end,
                month: selectedMonth.format('YYYY-MM') // Add month to ensure queryParams changes
            },
            loadPagination: true, // Use pagination mode to replace data instead of appending
            transformResponse: (response: any) => {
                // Handle different response structures
                let timesheets = [];
                let total = 0;

                if (response?.code === 200) {
                    // API returns: { code: 200, data: { timesheets: [...], total: ... } }
                    if (response.data?.timesheets) {
                        timesheets = response.data.timesheets;
                        total = response.data.total || timesheets.length;
                    } else if (Array.isArray(response.data)) {
                        // Direct array response
                        timesheets = response.data;
                        total = timesheets.length;
                    } else if (response.data?.data) {
                        // Nested data structure
                        timesheets = Array.isArray(response.data.data) ? response.data.data : [];
                        total = response.data.total || timesheets.length;
                    }
                }

                // Deduplicate by _id to prevent duplicate records
                const uniqueTimesheets = timesheets.filter((item: any, index: number, self: any[]) => {
                    const id = item._id || item.id;
                    if (!id) return true; // Keep items without ID
                    return index === self.findIndex((t: any) => (t._id || t.id) === id);
                });

                return {
                    data: uniqueTimesheets,
                    total: uniqueTimesheets.length
                };
            },
            title: 'Timesheet',
            isActionTab: true,
            setInitialForm: () => {
                // This will be called to trigger form opening
            },
            modalTitle: (actionType: string) => {
                return actionType === 'Add' ? 'Add Timesheet' : actionType === 'Edit' ? 'Edit Timesheet' : 'View Timesheet';
            },
            modalProps: isMobile ? {
                width: '100%',
                style: {
                    top: 'auto',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    paddingBottom: 0,
                    maxWidth: '100%',
                    margin: 0,
                    marginBottom: 0,
                    borderRadius: '20px 20px 0 0',
                    maxHeight: '90vh',
                    position: 'fixed'
                },
                wrapClassName: 'mobile-bottom-modal',
                bodyStyle: {
                    padding: '24px 20px',
                    paddingBottom: '24px',
                    maxHeight: 'calc(90vh - 100px)',
                    overflowY: 'auto',
                    background: '#ffffff',
                    borderRadius: '20px 20px 0 0',
                    margin: 0,
                    marginBottom: 0
                },
                maskStyle: {
                    backgroundColor: 'rgba(0, 0, 0, 0.6)'
                },
                okText: 'Save',
                cancelText: 'Cancel',
                transitionName: 'slide-up'
            } : undefined,
            getColumns: () => [
                {
                    title: 'Date',
                    dataIndex: 'date',
                    key: 'date',
                    // No sorter - sorting is handled by API (date: -1, newest first)
                    render: (text: string, record: any) => {
                        const date = moment(text);
                        const dayOfWeek = date.day();
                        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                        const isLeaveEntry = record.isLeave;
                        return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span>{date.format('DD MMM YYYY')}</span>
                                {isLeaveEntry && (
                                    <Tag color="red" icon={<CalendarOutlined />}>
                                        Leave
                                    </Tag>
                                )}
                                {record.isHoliday && (
                                    <Tag color="gold" icon={<CalendarOutlined />}>
                                        Holiday
                                    </Tag>
                                )}
                                {isWeekend && !isLeaveEntry && !record.isHoliday && (
                                    <Tag color="orange" icon={<CalendarOutlined />}>
                                        Weekend
                                    </Tag>
                                )}
                            </div>
                        );
                    }
                },
                {
                    title: 'Project Name',
                    dataIndex: 'projectName',
                    key: 'projectName',
                    render: (text: string, record: any) => {
                        if (record.isLeave) {
                            return <Tag color="red">Leave</Tag>;
                        }
                        if (record.isHoliday) {
                            return <Tag color="gold">Holiday</Tag>;
                        }
                        return text;
                    },
                    sorter: true
                },
                {
                    title: 'Module',
                    dataIndex: 'module',
                    key: 'module'
                },
                {
                    title: 'Task Details',
                    dataIndex: 'taskDetails',
                    key: 'taskDetails',
                    ellipsis: true
                },
                {
                    title: 'Worked Hours',
                    dataIndex: 'workedHours',
                    key: 'workedHours',
                    align: 'right',
                    render: (text: number) => `${text.toFixed(2)} hrs`,
                    sorter: true
                },
                {
                    title: 'Approval',
                    dataIndex: 'approvalAttachment',
                    key: 'approvalAttachment',
                    align: 'center',
                    width: 150,
                    render: (text: string, record: any) => {
                        // Check if this is a weekend or leave entry
                        const isWeekend = record.isWeekend || (() => {
                            if (record.date) {
                                const date = moment(record.date);
                                const dayOfWeek = date.day();
                                return dayOfWeek === 0 || dayOfWeek === 6; // 0 = Sunday, 6 = Saturday
                            }
                            return false;
                        })();
                        const isLeave = record.isLeave;

                        // Only show approval options for weekend or leave entries
                        if (!isWeekend && !isLeave) {
                            return <span>-</span>;
                        }

                        const recordId = record._id || record.id;
                        const hasAttachment = text && text.trim() !== '';

                        // Construct full URL for viewing
                        const getFileUrl = (filePath: string) => {
                            if (!filePath) return '';
                            // If already a full URL, return as is
                            if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
                                return filePath;
                            }
                            // If starts with /static/, use FILE_URL or API_URL
                            if (filePath.startsWith('/static/')) {
                                return `${process.env.FILE_URL || process.env.API_URL}${filePath}`;
                            }
                            // If it's just a filename, construct the full path
                            if (!filePath.startsWith('/')) {
                                return `${process.env.FILE_URL || process.env.API_URL}/static/${filePath}`;
                            }
                            // Otherwise, prepend FILE_URL or API_URL
                            return `${process.env.FILE_URL || process.env.API_URL}${filePath}`;
                        };

                        if (hasAttachment) {
                            const fileUrl = getFileUrl(text);
                            return (
                                <Button
                                    type="link"
                                    icon={<FileOutlined />}
                                    onClick={() => window.open(fileUrl, '_blank')}
                                    style={{ padding: 0 }}
                                    size="small"
                                >
                                    View
                                </Button>
                            );
                        } else {
                            return (
                                <Upload
                                    showUploadList={false}
                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                    beforeUpload={(file) => {
                                        handleApprovalUpload(file, recordId);
                                        return false;
                                    }}
                                >
                                    <Button
                                        type="default"
                                        icon={<UploadOutlined />}
                                        size="small"
                                        loading={uploadingAttachment === recordId}
                                    >
                                        {uploadingAttachment === recordId ? 'Uploading...' : 'Upload'}
                                    </Button>
                                </Upload>
                            );
                        }
                    }
                }
            ],
            getFormColumns: () => [
                {
                    span: 24,
                    type: 'custom',
                    formItem: () => {
                        const alerts = [];
                        if (isWeekendSelected) {
                            alerts.push(
                                <Alert
                                    key="weekend"
                                    message="Weekend Selected"
                                    description="The selected day is a weekend. You will be required to submit an approval screenshot for this work."
                                    type="warning"
                                    showIcon
                                    style={{ marginTop: 8, marginBottom: 8 }}
                                />
                            );
                        }
                        if (isLeaveValue) {
                            alerts.push(
                                <Alert
                                    key="leave"
                                    message="Leave Selected"
                                    description="You have marked this day as leave. You will be required to submit an approval proof for this leave."
                                    type="warning"
                                    showIcon
                                    style={{ marginTop: alerts.length > 0 ? 0 : 8, marginBottom: 8 }}
                                />
                            );
                        }
                        if (existingTimesheetDate) {
                            alerts.push(
                                <Alert
                                    key="existing"
                                    message="Timesheet Already Exists"
                                    description={`A timesheet entry already exists for ${moment(existingTimesheetDate).format('DD MMM YYYY')}. Submitting this form will create a duplicate entry.`}
                                    type="info"
                                    showIcon
                                    style={{ marginTop: alerts.length > 0 ? 0 : 8 }}
                                />
                            );
                        }
                        return alerts.length > 0 ? <div>{alerts}</div> : null;
                    }
                } as FormColumnConfig,
                {
                    name: 'entryType',
                    label: 'Entry Type',
                    type: 'custom',
                    span: isMobile ? 24 : 8,
                    formItem: (formItemProps: any, form: any) => {
                        return (
                            <Form.Item {...formItemProps} initialValue="workday">
                                <Select
                                    style={{ width: '100%' }}
                                    options={[
                                        { label: 'Workday', value: 'workday' },
                                        { label: 'Leave', value: 'leave' },
                                        { label: 'Holiday', value: 'holiday' }
                                    ]}
                                    value={entryType}
                                    onChange={(value: 'workday' | 'leave' | 'holiday') => {
                                        setEntryType(value);
                                        form.setFieldsValue({ entryType: value });

                                        if (value === 'leave' || value === 'holiday') {
                                            // Set worked hours to 0 and set default values
                                            form.setFieldsValue({
                                                workedHours: 0,
                                                module: value === 'leave' ? 'Leave' : 'Holiday',
                                                taskDetails: value === 'leave' ? 'Leave for the day' : 'Holiday for the day',
                                                projectName: value === 'leave' ? 'Leave' : 'Holiday'
                                            });
                                        } else {
                                            // Clear fields when switching back to workday
                                            form.setFieldsValue({
                                                module: undefined,
                                                taskDetails: undefined,
                                                workedHours: undefined,
                                                projectName: undefined
                                            });
                                        }
                                    }}
                                />
                            </Form.Item>
                        );
                    }
                } as FormColumnConfig,
                {
                    name: 'projectName',
                    label: 'Project Name',
                    type: 'select',
                    isCombo: true,
                    rules: [{ required: true, message: 'Project name is required' }],
                    placeholder: isLeaveValue ? 'Leave' : isHolidayValue ? 'Holiday' : 'Select or type new project name',
                    span: isMobile ? 24 : 8,
                    selectProps: {
                        showSearch: true,
                        allowClear: true,
                        mode: 'tags',
                        maxTagCount: 1,
                        tokenSeparators: [],
                        filterOption: false,
                        notFoundContent: null,
                        style: { width: '100%' },
                        dropdownStyle: { zIndex: 1050 },
                        disabled: isLeaveValue || isHolidayValue
                    }
                } as FormColumnConfig,
                {
                    name: 'date',
                    label: 'Date',
                    type: 'date',
                    rules: [{ required: true, message: 'Date is required' }],
                    initialValue: dayjs(),
                    span: isMobile ? 24 : 8,
                    dateProps: {
                        format: 'YYYY-MM-DD',
                        style: { width: '100%' },
                        placeholder: 'Select date',
                        allowClear: true,
                        getPopupContainer: (trigger: any) => trigger.parentElement || document.body,
                        onChange: (date: any) => {
                            if (date) {
                                const dayOfWeek = date.day();
                                const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // 0 = Sunday, 6 = Saturday
                                setIsWeekendSelected(isWeekend);
                                // Check if timesheet already exists for this date
                                checkExistingTimesheetForDate(date);
                            } else {
                                setIsWeekendSelected(false);
                                setExistingTimesheetDate(null);
                            }
                        }
                    }
                } as FormColumnConfig,
                {
                    name: 'module',
                    label: 'Module',
                    type: 'text',
                    rules: [{ required: !isLeaveValue && !isHolidayValue, message: 'Module is required' }],
                    placeholder: 'Enter module name',
                    span: isMobile ? 24 : 8,
                    disabled: isLeaveValue || isHolidayValue
                } as FormColumnConfig,
                {
                    name: 'workedHours',
                    label: 'Worked Hours',
                    type: 'number',
                    rules: [
                        { required: !isLeaveValue && !isHolidayValue, message: 'Worked hours are required' },
                        { type: 'number', min: 0, max: 24, message: 'Hours must be between 0 and 24' }
                    ],
                    placeholder: 'Enter hours worked',
                    span: isMobile ? 24 : 8,
                    disabled: isLeaveValue || isHolidayValue,
                    inputNumberProps: {
                        min: 0,
                        max: 24,
                        step: 0.5,
                        precision: 2,
                        style: { width: '100%' }
                    }
                } as FormColumnConfig,
                {
                    name: 'taskDetails',
                    label: 'Task Details',
                    type: 'textarea',
                    rules: [{ required: !isLeaveValue && !isHolidayValue, message: 'Task details are required' }],
                    placeholder: 'Describe the task you worked on',
                    span: isMobile ? 24 : 16,
                    disabled: isLeaveValue || isHolidayValue,
                    inputTextAreaProps: {
                        rows: 4,
                        style: { resize: 'vertical' }
                    }
                } as FormColumnConfig
            ],
            combo: [
                {
                    api: (() => {
                        const params = new URLSearchParams({
                            accessToken: accessToken || ''
                        });
                        return `/timesheet/public/project-names?${params.toString()}`;
                    })(),
                    name: 'projectName',
                    idKey: 'name',
                    render: (text) => {
                        if (!text) return '';
                        if (typeof text === 'string') return text;
                        return text?.name || text?._id || text || '';
                    },
                    rerender: []
                }
            ],
            onSubmit: async (id: any, values: any) => {
                // Convert projectName to string if it's an array (from tags mode)
                const projectName = Array.isArray(values.projectName)
                    ? values.projectName[0] || values.projectName.join(', ')
                    : values.projectName;

                const entryTypeValue = values.entryType || entryType;
                const isLeaveValue = entryTypeValue === 'leave';
                const isHolidayValue = entryTypeValue === 'holiday';

                const payload = {
                    accessToken: accessToken,
                    projectName: projectName,
                    date: values.date.format('YYYY-MM-DD'),
                    module: isLeaveValue ? 'Leave' : (isHolidayValue ? 'Holiday' : values.module),
                    taskDetails: isLeaveValue ? 'Leave for the day' : (isHolidayValue ? 'Holiday for the day' : values.taskDetails),
                    workedHours: isLeaveValue || isHolidayValue ? 0 : values.workedHours,
                    isLeave: isLeaveValue,
                    isHoliday: isHolidayValue
                };
                const result = await dispatch(postData('/timesheet/public/submit', payload));
                return result;
            },
            afterSubmit: (data: any) => {
                // Dispatch Redux action to refresh table
                dispatch({
                    type: 'REFRESH_TABLE',
                    refreshTable: true
                });
                // Refresh mobile cards if on mobile
                if (isMobile) {
                    loadTimesheets();
                    setFormVisible(false);
                }
                // Also reload project names if available
                if (data?.timesheet?.projectName) {
                    // Project names will be reloaded when form reopens
                }
            },
            actions: [
                {
                    actionType: 'Add',
                    name: 'Add Timesheet'
                }
            ],
            extraControles: [
                <Button
                    key="export"
                    icon={<FileExcelOutlined />}
                    onClick={handleExport}
                    type="default"
                >
                    Export to Excel
                </Button>,
                <Button
                    key="bulk-upload"
                    icon={<UploadOutlined />}
                    onClick={() => setUploadModalVisible(true)}
                    type="default"
                >
                    Bulk Upload
                </Button>
            ],
            onDataLoad: (data: any) => {
                // Transform data if needed when it loads
                return data;
            },
            renderSummary: () => {
                return (
                    <Card
                        style={{
                            marginBottom: '16px',
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            border: '1px solid #e8eaed',
                            background: '#ffffff'
                        }}
                        bodyStyle={{ padding: '16px 20px' }}
                    >
                        <Space size="middle" align="center" style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                            <Space size="middle" align="center">
                                <CalendarOutlined style={{ fontSize: '16px', color: '#667eea' }} />
                                <Text strong style={{ fontSize: '15px', color: '#1a1a1a' }}>Filter by Month:</Text>
                                <DatePicker
                                    picker="month"
                                    value={selectedMonth}
                                    onChange={(date) => {
                                        if (date) {
                                            setSelectedMonth(date);
                                        }
                                    }}
                                    format="MMMM YYYY"
                                    allowClear={false}
                                    style={{
                                        width: '200px',
                                        borderRadius: '8px'
                                    }}
                                    suffixIcon={<CalendarOutlined style={{ color: '#667eea' }} />}
                                />
                            </Space>
                            <Text type="secondary" style={{ fontSize: '13px' }}>
                                Showing timesheets for {selectedMonth.format('MMMM YYYY')}
                            </Text>
                        </Space>
                    </Card>
                );
            }
        };
    }, [accessToken, selectedMonth, isMobile, isWeekendSelected, entryType, existingTimesheetDate, uploadingAttachment]);

    const renderMobileCardView = () => {
        if (loading) {
            return (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spin size="large" />
                </div>
            );
        }

        if (timesheets.length === 0) {
            return (
                <Empty
                    description="No timesheet entries for this month"
                    style={{ padding: '40px 0' }}
                />
            );
        }

        return (
            <Row gutter={[16, 16]}>
                {timesheets.map((item: any) => (
                    <Col xs={24} key={item._id || item.id || Math.random()}>
                        <Card
                            hoverable
                            style={{
                                borderRadius: '12px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                border: '1px solid #e8eaed',
                                position: 'relative'
                            }}
                            bodyStyle={{ padding: '16px' }}
                        >
                            {/* Menu icon at top right corner */}
                            {(item.isWeekend || item.isLeave) && (() => {
                                const recordId = item._id || item.id;
                                const hasAttachment = item.approvalAttachment && item.approvalAttachment.trim() !== '';

                                // Construct full URL for viewing
                                const getFileUrl = (filePath: string) => {
                                    if (!filePath) return '';
                                    // If already a full URL, return as is
                                    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
                                        return filePath;
                                    }
                                    // If starts with /static/, use FILE_URL or API_URL
                                    if (filePath.startsWith('/static/')) {
                                        return `${process.env.FILE_URL || process.env.API_URL}${filePath}`;
                                    }
                                    // If it's just a filename, construct the full path
                                    if (!filePath.startsWith('/')) {
                                        return `${process.env.FILE_URL || process.env.API_URL}/static/${filePath}`;
                                    }
                                    // Otherwise, prepend FILE_URL or API_URL
                                    return `${process.env.FILE_URL || process.env.API_URL}${filePath}`;
                                };

                                const menuItems = [];

                                if (hasAttachment) {
                                    const fileUrl = getFileUrl(item.approvalAttachment);
                                    menuItems.push({
                                        key: 'view',
                                        label: 'View Approval',
                                        icon: <EyeOutlined />,
                                        onClick: () => window.open(fileUrl, '_blank')
                                    });
                                }

                                menuItems.push({
                                    key: 'upload',
                                    label: hasAttachment ? 'Replace Approval' : 'Upload Approval',
                                    icon: <UploadOutlined />,
                                    onClick: () => {
                                        // Create a hidden file input and trigger it
                                        const input = document.createElement('input');
                                        input.type = 'file';
                                        input.accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png';
                                        input.onchange = (e: any) => {
                                            const file = e.target.files?.[0];
                                            if (file) {
                                                handleApprovalUpload(file, recordId);
                                            }
                                        };
                                        input.click();
                                    }
                                });

                                return (
                                    <Dropdown
                                        menu={{ items: menuItems }}
                                        trigger={['click']}
                                        placement="bottomRight"
                                    >
                                        <Button
                                            type="text"
                                            icon={<MoreOutlined />}
                                            size="small"
                                            style={{
                                                position: 'absolute',
                                                top: '8px',
                                                right: '8px',
                                                padding: '4px 8px',
                                                minWidth: 'auto',
                                                height: 'auto'
                                            }}
                                        />
                                    </Dropdown>
                                );
                            })()}

                            <div style={{ marginBottom: '12px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Tag
                                    color="blue"
                                    icon={<CalendarOutlined />}
                                    style={{ fontSize: '12px' }}
                                >
                                    {moment(item.date).format('DD MMM YYYY')}
                                </Tag>
                                {item.isLeave && (
                                    <Tag color="red" icon={<CalendarOutlined />} style={{ fontSize: '12px' }}>
                                        Leave
                                    </Tag>
                                )}
                                {item.isHoliday && (
                                    <Tag color="gold" icon={<CalendarOutlined />} style={{ fontSize: '12px' }}>
                                        Holiday
                                    </Tag>
                                )}
                                {item.isWeekend && !item.isLeave && !item.isHoliday && (
                                    <Tag color="orange" icon={<CalendarOutlined />} style={{ fontSize: '12px' }}>
                                        Weekend
                                    </Tag>
                                )}
                            </div>
                            <div style={{ marginBottom: '12px' }}>
                                <Text strong style={{ fontSize: '16px', color: '#1a1a1a' }}>
                                    {item.isLeave ? 'Leave' : (item.isHoliday ? 'Holiday' : item.projectName)}
                                </Text>
                            </div>
                            {!item.isLeave && !item.isHoliday && (
                                <>
                                    <div style={{ marginBottom: '8px' }}>
                                        <Text type="secondary" style={{ fontSize: '13px' }}>
                                            <ProjectOutlined style={{ marginRight: '6px' }} />
                                            Module: {item.module}
                                        </Text>
                                    </div>
                                    <div style={{ marginBottom: '12px' }}>
                                        <Text style={{ fontSize: '13px', color: '#595959' }}>
                                            {item.taskDetails}
                                        </Text>
                                    </div>
                                </>
                            )}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                paddingTop: '12px',
                                borderTop: '1px solid #f0f0f0'
                            }}>
                                {!item.isLeave && !item.isHoliday && (
                                    <Tag
                                        color="green"
                                        icon={<ClockCircleOutlined />}
                                        style={{ fontSize: '13px', fontWeight: 500 }}
                                    >
                                        {item.workedHours?.toFixed(2) || '0.00'} hrs
                                    </Tag>
                                )}
                                {item.isLeave && (
                                    <Tag color="red" icon={<ClockCircleOutlined />} style={{ fontSize: '13px', fontWeight: 500 }}>
                                        Leave Day
                                    </Tag>
                                )}
                                {item.isHoliday && (
                                    <Tag color="gold" icon={<ClockCircleOutlined />} style={{ fontSize: '13px', fontWeight: 500 }}>
                                        Holiday
                                    </Tag>
                                )}
                                {(item.isWeekend || item.isLeave) && !item.isHoliday && !item.approvalAttachment && (
                                    <Tag color="red" icon={<PaperClipOutlined />} style={{ fontSize: '12px' }}>
                                        Missing Approval
                                    </Tag>
                                )}
                            </div>
                        </Card>
                    </Col>
                ))}
            </Row>
        );
    };

    return (
        <div style={{
            padding: isMobile ? '16px' : '32px',
            background: '#f5f7fa',
            minHeight: '100vh'
        }}>
            {isMobile ? (
                <div>
                    <Card
                        style={{
                            marginBottom: '20px',
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            border: '1px solid #e8eaed',
                            background: '#ffffff'
                        }}
                        bodyStyle={{ padding: '16px' }}
                    >
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '12px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                <Title level={4} style={{ margin: 0, fontSize: '18px', color: '#1a1a1a' }}>
                                    Timesheet Entries
                                </Title>
                                <div style={{
                                    padding: '4px 12px',
                                    background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eaed 100%)',
                                    borderRadius: '6px',
                                    border: '1px solid #e8eaed'
                                }}>
                                    <Space size="small" align="center">
                                        <CalendarOutlined style={{ fontSize: '14px', color: '#667eea' }} />
                                        <Text style={{ fontSize: '13px', color: '#595959' }}>
                                            {selectedMonth.format('MMMM YYYY')}
                                        </Text>
                                    </Space>
                                </div>
                            </div>
                            <Space size="small" wrap>
                                <DatePicker
                                    picker="month"
                                    value={selectedMonth}
                                    onChange={(date) => {
                                        if (date) {
                                            setSelectedMonth(date);
                                        }
                                    }}
                                    format="MMMM YYYY"
                                    allowClear={false}
                                    style={{ width: isMobile ? '140px' : '160px', borderRadius: '8px' }}
                                    size="small"
                                />
                                <Button
                                    icon={<FileExcelOutlined />}
                                    onClick={handleExport}
                                    type="default"
                                    size="small"
                                >
                                    Export
                                </Button>
                                <Button
                                    icon={<UploadOutlined />}
                                    onClick={() => setUploadModalVisible(true)}
                                    type="default"
                                    size="small"
                                >
                                    Upload
                                </Button>
                            </Space>
                        </div>
                    </Card>
                    {renderMobileCardView()}
                    {/* FAB Button for Mobile */}
                    {isMobile && (
                        <Button
                            type="primary"
                            shape="circle"
                            size="large"
                            icon={<PlusOutlined />}
                            onClick={() => setFormVisible(true)}
                            style={{
                                position: 'fixed',
                                bottom: '24px',
                                right: '24px',
                                width: '56px',
                                height: '56px',
                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                border: 'none',
                                zIndex: 1000,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '24px'
                            }}
                        />
                    )}
                    {/* Mobile Form Modal - Using MasterComponent's own modal */}
                    {formVisible && (
                        <MobileFormWrapper
                            config={config}
                            onClose={() => {
                                setFormVisible(false);
                                loadTimesheets();
                            }}
                        />
                    )}
                </div>
            ) : (
                <MasterComponent key={`timesheet-${selectedMonth.format('YYYY-MM')}`} config={config} />
            )}
            <Modal
                title={
                    <span style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <UploadOutlined /> Bulk Upload Timesheet
                    </span>
                }
                open={uploadModalVisible}
                onCancel={() => {
                    setUploadModalVisible(false);
                    setMappingStep('upload');
                    setExcelFile(null);
                    setExcelColumns([]);
                    setExcelData([]);
                    setColumnMapping({});
                    setDateFormat('auto');
                    setFixedValues({});
                    setFieldMappingMode({});
                }}
                footer={null}
                width={isMobile ? '95%' : mappingStep === 'mapping' ? 900 : 600}
                style={{ borderRadius: '12px' }}
            >
                <div style={{ padding: '8px 0' }}>
                    <p style={{ color: '#595959', marginBottom: 20, fontSize: '15px' }}>
                        Upload a CSV file with timesheet data. Download template for reference.
                    </p>
                    <div style={{ marginBottom: 20 }}>
                        <Button
                            onClick={downloadTemplate}
                            type="link"
                            style={{
                                padding: 0,
                                height: 'auto',
                                fontSize: '14px',
                                fontWeight: 500
                            }}
                        >
                            <DownloadOutlined /> Download CSV Template
                        </Button>
                    </div>
                    {mappingStep === 'upload' && (
                        <>
                            <Upload
                                accept=".csv,.xlsx,.xls"
                                beforeUpload={(file) => {
                                    handleFileUpload(file);
                                    return false; // Prevent auto upload
                                }}
                                showUploadList={false}
                            >
                                <Button
                                    icon={<UploadOutlined />}
                                    loading={uploadLoading}
                                    type="primary"
                                    size="large"
                                    style={{
                                        borderRadius: '8px',
                                        height: '44px',
                                        fontSize: '15px',
                                        fontWeight: 500,
                                        boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                                    }}
                                >
                                    Select File (CSV/Excel)
                                </Button>
                            </Upload>
                            <div style={{
                                marginTop: 20,
                                padding: 16,
                                background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eaed 100%)',
                                borderRadius: '8px',
                                border: '1px solid #e8eaed'
                            }}>
                                <p style={{ margin: 0, fontSize: '13px', color: '#595959', fontWeight: 500 }}>
                                    <strong style={{ color: '#1a1a1a' }}>Required Columns:</strong> Project Name, Date (YYYY-MM-DD), Module, Task Details, Worked Hours
                                </p>
                            </div>
                        </>
                    )}

                    {mappingStep === 'mapping' && (
                        <div>
                            <Alert
                                message="Configure Timesheet Fields"
                                description="For each field, choose to either map from Excel column or enter a fixed value that will apply to all rows."
                                type="info"
                                showIcon
                                style={{ marginBottom: 20 }}
                            />
                            <Card
                                title={<span style={{ fontSize: '14px', fontWeight: 600 }}>Timesheet Field Configuration</span>}
                                size="small"
                                style={{ marginBottom: 20 }}
                            >
                                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                    {requiredColumns.map((reqCol, index) => {
                                        const currentMode = fieldMappingMode[reqCol.key] || 'map';
                                        const isDateField = reqCol.key === 'date';

                                        return (
                                            <div
                                                key={index}
                                                style={{
                                                    padding: '12px',
                                                    marginBottom: '12px',
                                                    background: reqCol.required ? '#fff7e6' : '#f5f7fa',
                                                    borderRadius: '8px',
                                                    border: `1px solid ${reqCol.required ? '#ffd591' : '#e8eaed'}`,
                                                    fontSize: '13px'
                                                }}
                                            >
                                                <div style={{ marginBottom: '8px', fontWeight: 600, color: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <span>
                                                        {reqCol.label}
                                                        {reqCol.required && <span style={{ color: '#ff4d4f', marginLeft: '4px' }}>*</span>}
                                                    </span>
                                                </div>

                                                {/* Mode selector: Map from Excel or Fixed Value */}
                                                <div style={{ marginBottom: '8px' }}>
                                                    <Space>
                                                        <Button
                                                            size="small"
                                                            type={currentMode === 'map' ? 'primary' : 'default'}
                                                            onClick={() => {
                                                                setFieldMappingMode({
                                                                    ...fieldMappingMode,
                                                                    [reqCol.key]: 'map'
                                                                });
                                                                // Clear fixed value when switching to map mode
                                                                const newFixedValues = { ...fixedValues };
                                                                delete newFixedValues[reqCol.key];
                                                                setFixedValues(newFixedValues);
                                                            }}
                                                        >
                                                            Map from Excel
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            type={currentMode === 'fixed' ? 'primary' : 'default'}
                                                            onClick={() => {
                                                                setFieldMappingMode({
                                                                    ...fieldMappingMode,
                                                                    [reqCol.key]: 'fixed'
                                                                });
                                                                // Clear mapping when switching to fixed mode
                                                                const newMapping = { ...columnMapping };
                                                                delete newMapping[reqCol.key];
                                                                setColumnMapping(newMapping);
                                                            }}
                                                        >
                                                            Enter Fixed Value
                                                        </Button>
                                                    </Space>
                                                </div>

                                                {/* Map from Excel option */}
                                                {currentMode === 'map' && (
                                                    <div>
                                                        <Select
                                                            style={{ width: '100%' }}
                                                            placeholder="Select Excel column"
                                                            value={columnMapping[reqCol.key] || undefined}
                                                            onChange={(value) => {
                                                                const newMapping = {
                                                                    ...columnMapping,
                                                                    [reqCol.key]: value
                                                                };
                                                                setColumnMapping(newMapping);

                                                                // Auto-detect date format when date column is selected
                                                                if (isDateField && value && excelData.length > 0) {
                                                                    const dateColumn = value;
                                                                    const sampleDate = excelData[0]?.[dateColumn];
                                                                    if (sampleDate) {
                                                                        const detectedFormat = detectDateFormat(sampleDate);
                                                                        setDateFormat(detectedFormat);
                                                                    }
                                                                }
                                                            }}
                                                            allowClear
                                                            size="small"
                                                        >
                                                            {excelColumns.map((excelCol) => (
                                                                <Select.Option key={excelCol} value={excelCol}>
                                                                    {excelCol}
                                                                </Select.Option>
                                                            ))}
                                                        </Select>
                                                        {isDateField && columnMapping[reqCol.key] && (
                                                            <div style={{ marginTop: '8px' }}>
                                                                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                                                                    Date Format:
                                                                </Text>
                                                                <Select
                                                                    style={{ width: '100%' }}
                                                                    value={dateFormat}
                                                                    onChange={setDateFormat}
                                                                    size="small"
                                                                >
                                                                    <Select.Option value="auto">Auto-detect</Select.Option>
                                                                    <Select.Option value="excel">Excel Serial Number</Select.Option>
                                                                    <Select.Option value="YYYY-MM-DD">YYYY-MM-DD</Select.Option>
                                                                    <Select.Option value="DD/MM/YYYY">DD/MM/YYYY</Select.Option>
                                                                    <Select.Option value="MM/DD/YYYY">MM/DD/YYYY</Select.Option>
                                                                    <Select.Option value="DD-MM-YYYY">DD-MM-YYYY</Select.Option>
                                                                    <Select.Option value="MM-DD-YYYY">MM-DD-YYYY</Select.Option>
                                                                    <Select.Option value="YYYY/MM/DD">YYYY/MM/DD</Select.Option>
                                                                    <Select.Option value="DD.MM.YYYY">DD.MM.YYYY</Select.Option>
                                                                    <Select.Option value="MM.DD.YYYY">MM.DD.YYYY</Select.Option>
                                                                    <Select.Option value="DD MMM YYYY">DD MMM YYYY</Select.Option>
                                                                    <Select.Option value="MMM DD, YYYY">MMM DD, YYYY</Select.Option>
                                                                </Select>
                                                                {columnMapping.date && excelData.length > 0 && (
                                                                    <div style={{ marginTop: '4px', fontSize: '11px', color: '#8c8c8c' }}>
                                                                        Sample: {excelData[0]?.[columnMapping.date]} → {
                                                                            parseDate(excelData[0]?.[columnMapping.date], dateFormat) || 'Invalid date'
                                                                        }
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Fixed value option */}
                                                {currentMode === 'fixed' && (
                                                    <Input
                                                        placeholder={`Enter ${reqCol.label.toLowerCase()} (applies to all rows)`}
                                                        value={fixedValues[reqCol.key] || ''}
                                                        onChange={(e) => {
                                                            setFixedValues({
                                                                ...fixedValues,
                                                                [reqCol.key]: e.target.value
                                                            });
                                                        }}
                                                        size="small"
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </Card>

                            {/* Preview Section */}
                            <Card
                                title={<span style={{ fontSize: '14px', fontWeight: 600 }}>Preview (First 10 rows)</span>}
                                size="small"
                                style={{ marginBottom: 20 }}
                            >
                                {(() => {
                                    const previewRows = generatePreview();
                                    const errorCount = previewRows.filter(r => r.hasErrors).length;

                                    return (
                                        <div>
                                            {errorCount > 0 && (
                                                <Alert
                                                    message={`${errorCount} row(s) have errors. Please fix them before uploading.`}
                                                    type="error"
                                                    showIcon
                                                    style={{ marginBottom: 16 }}
                                                />
                                            )}
                                            <Table
                                                dataSource={previewRows}
                                                pagination={false}
                                                size="small"
                                                scroll={{ x: 'max-content' }}
                                                columns={[
                                                    {
                                                        title: 'Row',
                                                        dataIndex: 'rowNumber',
                                                        key: 'rowNumber',
                                                        width: 60,
                                                        render: (text: number, record: any) => (
                                                            <span style={{ color: record.hasErrors ? '#ff4d4f' : '#595959' }}>
                                                                {text}
                                                            </span>
                                                        )
                                                    },
                                                    {
                                                        title: 'Project Name',
                                                        dataIndex: 'projectName',
                                                        key: 'projectName',
                                                        width: 150,
                                                        render: (text) => text || <span style={{ color: '#ff4d4f' }}>Missing</span>
                                                    },
                                                    {
                                                        title: 'Date (Original)',
                                                        dataIndex: 'date',
                                                        key: 'date',
                                                        width: 120,
                                                        render: (text) => text || <span style={{ color: '#ff4d4f' }}>Missing</span>
                                                    },
                                                    {
                                                        title: 'Date (Parsed)',
                                                        dataIndex: 'dateParsed',
                                                        key: 'dateParsed',
                                                        width: 120,
                                                        render: (text: string, record: any) => {
                                                            if (!text || text === '❌ Invalid' || (typeof text === 'string' && text.startsWith('1970-01-01'))) {
                                                                return <span style={{ color: '#ff4d4f' }}>❌ Invalid</span>;
                                                            }
                                                            return <span style={{ color: '#52c41a' }}>✓ {text}</span>;
                                                        }
                                                    },
                                                    {
                                                        title: 'Module',
                                                        dataIndex: 'module',
                                                        key: 'module',
                                                        width: 150,
                                                        render: (text) => text || <span style={{ color: '#ff4d4f' }}>Missing</span>
                                                    },
                                                    {
                                                        title: 'Task Details',
                                                        dataIndex: 'taskDetails',
                                                        key: 'taskDetails',
                                                        width: 200,
                                                        render: (text) => (
                                                            <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {text || <span style={{ color: '#ff4d4f' }}>Missing</span>}
                                                            </div>
                                                        )
                                                    },
                                                    {
                                                        title: 'Worked Hours',
                                                        dataIndex: 'workedHours',
                                                        key: 'workedHours',
                                                        width: 100,
                                                        render: (text) => {
                                                            if (text === undefined || text === null || text === '') {
                                                                return <span style={{ color: '#ff4d4f' }}>Missing</span>;
                                                            }
                                                            if (isNaN(parseFloat(text))) {
                                                                return <span style={{ color: '#ff4d4f' }}>Invalid</span>;
                                                            }
                                                            return parseFloat(text).toFixed(2);
                                                        }
                                                    },
                                                    {
                                                        title: 'Errors',
                                                        key: 'errors',
                                                        width: 200,
                                                        render: (_: any, record: any) => (
                                                            <div>
                                                                {record.errors && record.errors.length > 0 ? (
                                                                    <div>
                                                                        {record.errors.map((error: string, idx: number) => (
                                                                            <Tag key={idx} color="red" style={{ marginBottom: '4px' }}>
                                                                                {error}
                                                                            </Tag>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <Tag color="green">✓ Valid</Tag>
                                                                )}
                                                            </div>
                                                        )
                                                    }
                                                ]}
                                            />
                                        </div>
                                    );
                                })()}
                            </Card>

                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                                <Button
                                    onClick={() => {
                                        setMappingStep('upload');
                                        setExcelFile(null);
                                        setExcelColumns([]);
                                        setExcelData([]);
                                        setColumnMapping({});
                                        setDateFormat('auto');
                                        setFixedValues({});
                                        setFieldMappingMode({});
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={handleBulkUpload}
                                    loading={uploadLoading}
                                    disabled={
                                        // Check if all required fields are either mapped or have fixed values
                                        !(
                                            (columnMapping.projectName || fixedValues.projectName) &&
                                            (columnMapping.date || fixedValues.date) &&
                                            (columnMapping.module || fixedValues.module) &&
                                            (columnMapping.taskDetails || fixedValues.taskDetails) &&
                                            (columnMapping.workedHours || fixedValues.workedHours)
                                        )
                                    }
                                >
                                    Submit Timesheet
                                </Button>
                            </div>
                        </div>
                    )}

                    {mappingStep === 'processing' && (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <Spin size="large" />
                            <p style={{ marginTop: 20, fontSize: '15px', color: '#595959' }}>
                                Processing timesheet entries...
                            </p>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default TimesheetList;
